import React, { useEffect, forwardRef } from 'react';
import { createPortal } from 'react-dom';
import { clsx } from 'clsx';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  children: React.ReactNode;
  closeOnOverlayClick?: boolean;
  closeOnEscape?: boolean;
  className?: string;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  size = 'md',
  children,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  className,
}) => {
  // Handle escape key
  useEffect(() => {
    if (!closeOnEscape || !isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [closeOnEscape, isOpen, onClose]);

  // Prevent body scroll and horizontal shifting when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'pan-y';
      document.body.classList.add('modal-open');
    } else {
      document.body.style.overflow = 'unset';
      document.body.style.touchAction = 'manipulation';
      document.body.classList.remove('modal-open');
    }

    return () => {
      document.body.style.overflow = 'unset';
      document.body.style.touchAction = 'manipulation';
      document.body.classList.remove('modal-open');
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'w-full sm:max-w-md',
    md: 'w-full sm:max-w-lg',
    lg: 'w-full sm:max-w-2xl',
    xl: 'w-full sm:max-w-4xl',
    full: 'max-w-[95vw] max-h-[95vh]',
  };

  const overlayClasses = [
    'fixed',
    'inset-0',
    'bg-black/60',
    'backdrop-blur-sm',
    'z-50',
    'flex',
    'items-center',
    'justify-center',
    'p-4',
    'overflow-x-hidden',
    'touch-pan-y',
    'animate-fade-in',
  ];

  const contentClasses = [
    'bg-card/95',
    'backdrop-blur-xl',
    'text-foreground',
    'border',
    'border-border/80',
    'rounded-3xl',
    'shadow-2xl',
    'w-full',
    'max-w-[calc(100vw-2rem)]',
    sizeClasses[size],
    'max-h-[90vh]',
    'max-h-[90dvh]',
    'flex',
    'flex-col',
    'overflow-hidden',
    'touch-pan-y',
    'animate-scale',
  ];

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (closeOnOverlayClick && e.target === e.currentTarget) {
      onClose();
    }
  };

  return createPortal(
    <div className={clsx(overlayClasses)} onClick={handleOverlayClick}>
      <div className={clsx(contentClasses, className)}>
        {children}
      </div>
    </div>,
    document.body
  );
};

// Modal sub-components
const ModalHeader = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }, ref) => (
    <div
      ref={ref}
      className={clsx(
        'flex items-center justify-between px-6 pt-6 pb-2',
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
);

ModalHeader.displayName = 'ModalHeader';

const ModalTitle = forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, children, ...props }, ref) => (
    <h2
      ref={ref}
      className={clsx('text-xl font-bold text-foreground tracking-tight', className)}
      {...props}
    >
      {children}
    </h2>
  )
);

ModalTitle.displayName = 'ModalTitle';

const ModalBody = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }, ref) => (
    <div
      ref={ref}
      className={clsx('px-6 py-4 text-foreground/90 overflow-y-auto overflow-x-hidden overscroll-contain flex-1 touch-pan-y', className)}
      {...props}
    >
      {children}
    </div>
  )
);

ModalBody.displayName = 'ModalBody';

const ModalFooter = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }, ref) => (
    <div
      ref={ref}
      className={clsx(
        'flex items-center justify-end gap-2 px-6 pb-6 pt-2',
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
);

ModalFooter.displayName = 'ModalFooter';

const ModalCloseButton = forwardRef<HTMLButtonElement, React.ButtonHTMLAttributes<HTMLButtonElement>>(
  ({ className, onClick, ...props }, ref) => (
    <button
      ref={ref}
      className={clsx(
        'rounded-md p-1 text-muted-foreground hover:text-foreground hover:bg-accent/10 transition-colors',
        className
      )}
      onClick={onClick}
      {...props}
    >
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
      <span className="sr-only">Close</span>
    </button>
  )
);

ModalCloseButton.displayName = 'ModalCloseButton';

export { Modal, ModalHeader, ModalTitle, ModalBody, ModalFooter, ModalCloseButton };